import type { TrialProcessOptions } from '../types/trial-options.js'
import type { SavedDesign } from '../schemas/design.js'
import { applyDesignBrandColor, DEFAULT_DESIGN } from './default-design.js'
import type { TokenResolutionData } from './design-tokens.js'

/**
 * Maps the public trial page onto the default design — the same document
 * newly created agencies are seeded with — so the trial preview and
 * download are produced by the exact same rendering path as a paying
 * agency's. `baseDesign` is the checked-in DEFAULT_DESIGN unless the
 * caller passes the admin-uploaded runtime default (see the server's
 * default-design-service), keeping the trial → signup → Customize flow
 * on one design.
 *
 * The design itself is static: the trial form's editable fields flow in
 * through `{{agency.*}}` / `{{recruiter.*}}` tokens (see
 * buildTrialTokenData), and the brand color the visitor picks is
 * substituted for the design's sentinel color. Lines whose tokens all
 * resolve empty are collapsed at render time, so cleared form fields drop
 * their line just like the old per-keystroke design rebuild did.
 */
export function buildTrialDesign(
  options: TrialProcessOptions,
  baseDesign: SavedDesign = DEFAULT_DESIGN
): SavedDesign {
  const brandColor =
    options.borderColor ?? options.agencyBrandColor ?? options.textColor
  return applyDesignBrandColor(baseDesign, brandColor)
}

/**
 * Token data for rendering a trial design: the trial form's values, keyed
 * by the tokens DEFAULT_DESIGN references. A trial session is anonymous, so
 * this is the only source tokens can resolve against.
 */
export function buildTrialTokenData(
  options: TrialProcessOptions
): TokenResolutionData {
  return {
    agency: {
      name: options.headerAgencyName ?? options.footerAgencyName,
      phone: options.headerAgencyPhoneNumber ?? options.footerAgencyPhoneNumber,
      url: options.headerAgencyUrl ?? options.footerAgencyUrl,
      email: options.headerAgencyEmail ?? options.footerAgencyEmail,
      linkedIn: options.headerAgencyLinkedIn ?? options.footerAgencyLinkedIn,
    },
    recruiter: {
      name: options.headerRecruiterName ?? options.footerRecruiterName,
      phone:
        options.headerRecruiterPhoneNumber ??
        options.footerRecruiterPhoneNumber,
      email: options.headerRecruiterEmail ?? options.footerRecruiterEmail,
      linkedIn:
        options.headerRecruiterLinkedIn ?? options.footerRecruiterLinkedIn,
    },
  }
}
